import React, { useRef, useState } from 'react';
import { parse, HANDLE_ERROR_RU } from '../../../shared/handle/index.js';
import { checkDuplicates, keepRawHandle } from '../api/leads.js';
import { PLATFORMS } from '../constants/platforms.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHECKABLE = [PLATFORMS.INSTAGRAM, PLATFORMS.TIKTOK, 'email'];

const VERDICT_TEXT = {
  ambiguous: 'Это Instagram или TikTok?',
  checking: 'Проверяем…',
  new: 'Дублей не найдено',
  unchecked: 'Проверим при добавлении',
  'check-failed': 'Не удалось проверить',
  'add-failed': 'Не удалось добавить',
};

const refusalText = (code, detected, platform) =>
  (HANDLE_ERROR_RU[code] ?? HANDLE_ERROR_RU.unparseable)
    .replace('{detected}', detected ?? '')
    .replace('{hint}', platform ?? detected ?? '');

/**
 * Raw string to ONE identifier field. Pure: no request, no state. Returns
 * `{kind:'idle'}` · `{kind:'email'|'handle', field, identifier, name, platform?}`
 * · `{kind:'ambiguous'}` (the IG/TT pill) · `{kind:'refused', message}`.
 */
export function classify (raw, platformPick) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { kind: 'idle' };

  const bare = !trimmed.includes('/');
  if (bare && EMAIL_RE.test(trimmed)) {
    return { kind: 'email', field: 'email', identifier: trimmed, name: trimmed.split('@')[0].trim() };
  }

  const result = parse(trimmed, platformPick || undefined);
  if (result.handle) {
    return { kind: 'handle', field: result.platform, platform: result.platform, identifier: result.handle, name: result.handle };
  }
  if (!platformPick && bare) return { kind: 'ambiguous' };
  return { kind: 'refused', message: refusalText(result.error, result.detected, platformPick) };
}

export default function QuickAddStrip ({ onSubmit, onOpenLead = null, disabled = false }) {
  const [value, setValue] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [platformPick, setPlatformPick] = useState(null);
  const [chip, setChip] = useState(null);
  const [adding, setAdding] = useState(false);

  const reqIdRef = useRef(0);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  // `paste` fires before the input's value changes, so the paste handler only sets
  // a flag that the next `onChange` consumes as delay 0.
  const pasteRef = useRef(false);

  const runCheck = async (raw, cls, id) => {
    setVerdict({ state: 'checking', forValue: raw });
    try {
      const field = cls.field;
      const identifier = cls.identifier;
      const result = await checkDuplicates({ [field]: identifier });
      if (id !== reqIdRef.current) return;
      if (result?.rejected?.length) {
        setVerdict({ state: 'refused', forValue: raw, message: result.rejected[0]?.reason || HANDLE_ERROR_RU.unparseable });
      } else if (result?.isDuplicate) {
        setVerdict({ state: 'duplicate', forValue: raw, duplicates: result.duplicates || [] });
      } else {
        setVerdict({ state: 'new', forValue: raw });
      }
    } catch (err) {
      console.error('Quick-add duplicate check failed', err);
      if (id !== reqIdRef.current) return;
      setVerdict({ state: 'check-failed', forValue: raw });
    }
  };

  const schedule = (raw, pick, delay) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const id = reqIdRef.current;
    debounceRef.current = setTimeout(() => {
      if (id !== reqIdRef.current) return;
      const cls = classify(raw, pick);
      if (cls.kind === 'idle') { setVerdict(null); return; }
      if (cls.kind === 'ambiguous') { setVerdict({ state: 'ambiguous', forValue: raw }); return; }
      if (cls.kind === 'refused') { setVerdict({ state: 'refused', forValue: raw, message: cls.message }); return; }
      if (!CHECKABLE.includes(cls.field)) { setVerdict({ state: 'unchecked', forValue: raw }); return; }
      runCheck(raw, cls, id);
    }, delay);
  };

  const handleChange = (event) => {
    const next = event.target.value;
    reqIdRef.current += 1;
    setValue(next);
    setPlatformPick(null);
    setVerdict(null);
    setChip(null);
    const immediate = pasteRef.current;
    pasteRef.current = false;
    schedule(next, null, immediate ? 0 : 500);
  };

  const handlePick = (platform) => {
    reqIdRef.current += 1;
    setPlatformPick(platform);
    setVerdict(null);
    schedule(value, platform, 0);
  };

  const handleRetry = () => {
    reqIdRef.current += 1;
    setVerdict(null);
    schedule(value, platformPick, 0);
  };

  const reset = () => {
    reqIdRef.current += 1;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setValue('');
    setVerdict(null);
    setPlatformPick(null);
    setChip(null);
    inputRef.current?.focus();
  };

  const trimmed = value.trim();
  const verdictIsCurrent = Boolean(verdict && verdict.forValue === value);
  const canSubmit = !disabled && !adding && trimmed !== '' && verdictIsCurrent
    && (verdict.state === 'new' || verdict.state === 'unchecked');

  /** The add itself: the form's submit (guarded by `canSubmit`) and the row-12
   *  «Повторить» of `add-failed`, retryable where 409/400 are not. */
  const doAdd = async () => {
    if (adding || disabled) return;
    const cls = classify(value, platformPick);
    if (cls.kind !== 'handle' && cls.kind !== 'email') return;
    const payload = cls.field === 'email'
      ? { name: cls.name, email: cls.identifier }
      : { name: cls.name, [cls.field]: keepRawHandle(value, cls.platform) };

    setAdding(true);
    const outcome = await onSubmit(payload);
    setAdding(false);

    if (outcome?.ok) {
      reqIdRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setValue('');
      setVerdict(null);
      setPlatformPick(null);
      setChip({ name: outcome.lead?.name || cls.name, leadId: outcome.lead?.id || null });
      inputRef.current?.focus();
      return;
    }
    const code = outcome?.code ?? null;
    const status = outcome?.status ?? null;
    if (code === 'duplicate' || status === 409) {
      setVerdict({ state: 'duplicate', forValue: value, duplicates: outcome?.duplicate ? [outcome.duplicate] : [] });
    } else if (code === 'invalid_handle' || status === 400) {
      setVerdict({ state: 'refused', forValue: value, message: outcome?.reason || HANDLE_ERROR_RU.unparseable });
    } else {
      setVerdict({ state: 'add-failed', forValue: value, message: outcome?.reason || null });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    doAdd();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (adding) return;
      event.preventDefault();
      reset();
      return;
    }
    if (event.key === 'Enter' && !canSubmit) event.preventDefault();
  };

  const state = adding ? 'adding' : (verdictIsCurrent ? verdict.state : (trimmed === '' ? 'idle' : 'typing'));
  const dup = state === 'duplicate' ? (verdict.duplicates?.[0] || null) : null;
  const line = { fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '6px', alignItems: 'center' };

  return (
    <form className='quick-add' onSubmit={handleSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', minWidth: 0, maxWidth: '460px' }}>
      <input
        ref={inputRef}
        className='form-input'
        type='text'
        value={value}
        onChange={handleChange}
        onPaste={() => { pasteRef.current = true; }}
        onKeyDown={handleKeyDown}
        readOnly={adding}
        disabled={disabled}
        placeholder='Ссылка, @ник или email'
        style={{ flex: '1 1 220px', minWidth: 0, padding: '6px 12px', fontSize: '13px', borderRadius: '6px' }}
      />
      <button type='submit' className='board-action-btn board-action-btn--active' disabled={!canSubmit}>
        {adding ? 'Добавляем…' : 'Добавить'}
      </button>

      {(VERDICT_TEXT[state] || state === 'refused') && (
        <span className='quick-add__verdict' style={state === 'refused' ? { ...line, color: 'var(--red-60, #dc2626)' } : line}>
          {state === 'refused' ? verdict.message : VERDICT_TEXT[state]}
          {state === 'ambiguous' && (
            <>
              <button type='button' className='board-action-btn' onClick={() => handlePick(PLATFORMS.INSTAGRAM)}>IG</button>
              <button type='button' className='board-action-btn' onClick={() => handlePick(PLATFORMS.TIKTOK)}>TT</button>
            </>
          )}
          {state === 'check-failed' && <button type='button' className='board-action-btn' onClick={handleRetry}>Повторить</button>}
          {state === 'add-failed' && <button type='button' className='board-action-btn' onClick={doAdd}>Повторить</button>}
        </span>
      )}

      {/* AC-7 — one exit, the existing opener: navigate first, same page. No claim,
          no takeover, no "add anyway". `quick-add__resolution` is the named region the
          frozen claim path (`dedupGatekeeper.js:64-66`) lands in when unfrozen. */}
      {dup && (
        <div className='quick-add__resolution' style={line}>
          <span>
            {`Дубль: ${[dup.name, dup.ownerName, dup.statusLabel].filter(Boolean).join(' · ')}`}
            {verdict.duplicates?.length > 1 ? ` и ещё ${verdict.duplicates.length - 1}` : ''}
          </span>
          {dup.leadId && typeof onOpenLead === 'function' && <button type='button' className='board-action-btn' onClick={() => onOpenLead(dup.leadId)}>Открыть</button>}
        </div>
      )}

      {chip && !verdict && !adding && (
        <span className='quick-add__verdict' style={line}>
          {`Добавлен ${chip.name}`}
          {chip.leadId && typeof onOpenLead === 'function' && <button type='button' className='board-action-btn' onClick={() => onOpenLead(chip.leadId)}>Открыть</button>}
        </span>
      )}
    </form>
  );
}
